import {React, observer, useEffect, classNames, _, Suspense, Fragment, Popover2} from '@api'
import {Icon, Button, Menu, MenuItem, MenuDivider, Popover} from '@blueprintjs/core'
import {AnimatedNumber} from '@lazy'
import {AggregateDetailsStore, AggregateDetailsStoreContext, ToggleAggregationLocationButton, useAggregateDetailsStore} from '@modules/catalog'
import {GroupDetailAggregationFieldConfig, ui} from '@stores'
import {useVipLocalStore} from '@modules'
import {PrimaryIconButton, CollapsibleStateful} from 'centric-ui-components'
import * as css from './SidebarAggregateDetails.css'
import {Tooltip2} from '@api'

export const AggregationFilterCriteriaDropDown = observer(function AggregationFilterCriteriaDropDown({}) {
  const {
    strings, catalog: {settings: {aggregateDetailsSelectedSections}},
    aggregationFilterStore: {
      selectedAggFiltersSet, selectedAggFilterValues, setSelectedAggFilters: setSelectedAggFilters, availableAggFilterValues, selectedAggregationFilterField
    }
  } = useAggregateDetailsStore()

  if (_.isEmpty(availableAggFilterValues)) return null

  const dropDownOptions = availableAggFilterValues.map((item, i) => {
    return <MenuItem key={i}
                     className={css.clearMenuItem}
                     shouldDismissPopover={false}
                     text={item.label}
                     icon={selectedAggFiltersSet.has(item.id) ? 'tick' : 'blank'}
                     onClick={() => setSelectedAggFilters(item)}
    />
  })

  return (
    <Popover2
      usePortal={false}
      key={'menu'}
      minimal
      captureDismiss
      position='bottom-left'
      targetTagName='div'
      content={
        <Menu className={css.cardSizeDropDownMenu}>
          {dropDownOptions}
          {_.size(aggregateDetailsSelectedSections[selectedAggregationFilterField]) > 0 &&
		   <Fragment>
			   <MenuDivider/>
			   <MenuItem className={classNames(css.menu)} icon='clean' text={strings.clearSelection} onClick={() => aggregateDetailsSelectedSections[selectedAggregationFilterField] = []}/>
		   </Fragment>
          }
        </Menu>
      }>
      <Button rightIcon='caret-down'
              minimal
              className={css.aggFilterDropDown}
              text={selectedAggFilterValues}/>
    </Popover2>
  )
})

export const SidebarAggregateDetails = observer(function SidebarAggregateDetails() {
  const store = useVipLocalStore((stores) => new AggregateDetailsStore(stores, _.first(stores.catalog.settings.aggregateDetailsGroupPath))),
    {
      strings, catalog, config, showNavButtons, data, navigateToPreviousGroup, navigateToNextGroup, groupNode, title, count, aggregationFilterStore: {showAggregationFilter, selectedAggFilterValues},
      catalog: {settings, aggregation: {onToggleAggregateDetailsLocation, closeAggregateDetails}}
    } = store
  useEffect(() => {
    store._aggregationGroupPath = _.first(settings.aggregateDetailsGroupPath)
  }, [settings.aggregateDetailsGroupPath])

  useEffect(() => {
    (catalog.isProductView || groupNode?.store) && store.runQuery()
  }, [catalog.loadingCounts, catalog.isProductView, groupNode?.store, catalog.aggregation.aggregateDetailsGroup, catalog.es.refetchId, catalog.es.aggregateDetailsRefetchId, selectedAggFilterValues])

  //if (!groupNode?.store || !catalogConfig?.features?.groupBy?.detailAggregations) return null

  // const {store: {props: {info: {count, title}}}} = groupNode

  //console.log(toJS({config, aggregationFields, computationalFields, data}, {recurseEverything: true}))
  /*

        {pathIncludingSelf.map(({group, groupName, grouping}) => {
          return <div className={css.entry} key={groupName}>
            <label>{grouping.label}</label>
            <span className={css.value}>{title}</span>
          </div>
        })}
   */

  if (!groupNode?.store && !catalog.isProductView) {
    return <div className={css.root}>
      <span className={css.intentionallyBlank}>{strings.intentionallyBlank}</span>
    </div>
  }
  return (
    <Suspense fallback={null}>
      <AggregateDetailsStoreContext.Provider value={store}>
        <div className={css.root}>
          <div className={css.groupInfoPanel} data-has-group={groupNode != null}>
            <div className={css.groupTitle}>{groupNode?.store?.grouping?.fullTitle}</div>
            <div className={css.groupName}>{title}</div>
            {showAggregationFilter &&
			 <div className={css.groupSelection}>
				 <AggregationFilterCriteriaDropDown/>
			 </div>
            }
            <div className={css.close}>
              {/*<ToggleAggregationLocationButton/>*/}
              {groupNode || true ? <ToggleAggregationLocationButton/>
               :<PrimaryIconButton tooltip={strings.close} tooltipPosition='bottom' icon={<Icon icon='cross'/>} onClick={closeAggregateDetails}/>}
            </div>
            {/*<div className={css.count}>{count} {pluralize(catalog.workspaceConfig.itemName, count)}</div>*/}
            {showNavButtons &&
			 <div className={css.nav}>
				 <Tooltip2 content={strings.previousGroup}><PrimaryIconButton icon={<Icon icon='arrow-left'/>} onClick={navigateToPreviousGroup}/></Tooltip2>
				 <Tooltip2 content={strings.nextGroup}><PrimaryIconButton icon={<Icon icon='arrow-right'/>} onClick={navigateToNextGroup}/></Tooltip2>
			 </div>}
          </div>

          <div className={css.sections}>
            {(config?.sections ?? []).map(({fields, label = ''}, i) => {
              const children = fields
                  .filter(f => !f.hidden && catalog.settings.detailAggs[f.output ?? f.field] !== false)
                  .map((f, j) => <GroupAggregationFieldRenderer key={j} f={f}/>),
                onlyOneSection = config.sections.length === 1

              return _.isEmpty(children) ? null : (
                <CollapsibleStateful key={i}
                                     open={onlyOneSection ? true : undefined}
                                     className={css.collapsible} classes={{list: css.list, wrapperInner: css.listContent}}
                                     title={onlyOneSection ? null : label}
                                     hideExpandCollapseButton={onlyOneSection}
                                     data-no-label={onlyOneSection || !label}>
                  {children}
                </CollapsibleStateful>)
            })}
          </div>
        </div>
      </AggregateDetailsStoreContext.Provider>
    </Suspense>
  )
})

export const GroupAggregationFieldRenderer = observer<{ className?: string, f: GroupDetailAggregationFieldConfig }>(function GroupAggregationFieldRenderer({f, className}) {
  const {strings, catalog, data, formatNumberValue} = useAggregateDetailsStore(),
    catalogField: any = f.field ? catalog._fieldByName[f.field] : f,
    def = catalogField?.def ?? f.def,
    label = f.label ?? catalog.labelForFieldName_currentCatalog(f.field),
    d: any = _.get(data, f.output ?? f.field ?? f.label),
    value = d?.value ?? d,
    displayValue = _.isNaN(value)
                   ? strings.emptyLabel
                   : value == null
                     ? null
                     : _.isString(value) ? value : def?.precision != null || def?.type == 'Int'
                                                   ? value.toLocaleString(undefined, {maximumFractionDigits: def?.precision ?? 0})
                                                   : value.toLocaleString(undefined, {minimumFractionDigits: def?.precision ?? 2, maximumFractionDigits: def?.precision ?? 2})

  const computed = catalogField?.computed ?? f.computed

  // console.log(`render`, f.output, f.field, f, displayValue, value)
  return (
    <div className={classNames(css.entry, className)}>
      <label>{label}
        {computed?.flag ? (computed.flag({value}) && <Icon icon='flag' className={css.flag}/>) : null}</label>
      <div style={{color: computed?.color ? computed.color({value}) : 'var(--black)'}} data-type='value' className={css.value}>
        {f?.prefix ?? ''}{!_.isNil(displayValue) && ui.iconForCurrency(def?.currency, {className: css.currency})}
        {_.isNumber(value)
         ? <AnimatedNumber value={value} formatValue={formatNumberValue(f)} duration={500}/>
         : (displayValue ?? strings.emptyLabel)}
        {def?.suffix && displayValue != strings.emptyLabel ? def.suffix : ''}</div>
    </div>
  )
})



// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/GroupByCatalog_Groups/AggregateDetails/SidebarAggregateDetails.tsx